import React, { FC, useEffect, useMemo, useState } from 'react';
import Form from 'components/Form/Form';
import { Col, Row } from 'components/Grid/Grid';
import Select from 'components/Select/Select';
import { FormItem } from 'components/Form/FormItem';
import { Group, TextArea } from 'components/Input';
import { useSearchCompaniesInFilterQuery } from 'redux/apiSlices/company.slice';
import {
  useGetTimeEntryAssignmentsQuery,
  useGetWorkTypesQuery,
} from 'redux/apiSlices/timeEntry.slice';
import { validationRules } from 'components/Form/validationRules';
import { AssignmentEnumUi } from 'data/types/timeEntry.types';
import LabelWithStatus from 'components/LabelSelectOptions/LabelWithStatus';
import debounce from 'lodash/debounce';
import { useAppSelector } from 'redux/store';
import { Moment } from 'moment';
import { CompanyStatusMapperColor } from 'data/converters/company.converter';
import useWorkTypeNames from 'api/hooks/useWorkTypeNames';
import { getDateTimeFormats } from 'helpers/utils';
import { TCreateTimeEntryForm } from './CreateTimeEntryForm.type';
import { getStatusColor } from './CreateTimeEntry.data';
import { SCompactForm } from './CreateTimeEntryForm.style';
import DateTimeInput from './DateTimeInput';
import TimeRangeBar from './TimeRangeBar';
import { TViewMode } from './timeRange.utils';

const rules = {
  workType: [validationRules.required()],
  note: [validationRules.required()],
  companyId: [validationRules.required()],
  startTime: validationRules.startTime,
  endTime: validationRules.endTime,
};

// Work types that can't be attached to a ticket, task or project. These are catalog KEYS,
// which survive an MSP renaming or retiring the entry. A work type an MSP creates itself
// will never match, so it always allows an assignment.
//
// NOTE: the backend hardcodes work type keys too, in TimeEntry.setCompanyId — a time entry
// requires a company unless it is BREAK_LUNCH or TRAINING. That is a DIFFERENT pair from the
// one below, and the mismatch predates the move off the enum (ZEST-759). Both were enum
// references before and are now plain strings in two separate repos, so change one with the
// other in mind. Reconciling them is a product question, not a refactor.
const NON_ASSIGNABLE_WORK_TYPE_KEYS = {
  breakLunch: 'BREAK_LUNCH',
  paidTimeOff: 'PAID_TIME_OFF',
};

const CreateTimeEntryForm: FC<
  TCreateTimeEntryForm & { onSubmit?: () => void }
> = ({ formObj, initialValues, onSubmit }) => {
  const { timeFormat } = getDateTimeFormats();
  // Default the display mode to the org's clock convention (12h vs 24h),
  // but let the user flip it per ZEST-692 requirement #3.
  const [viewMode, setViewMode] = useState<TViewMode>(
    timeFormat.includes('A') ? '12' : '24',
  );
  const [start, setStart] = useState<Moment | null>(
    initialValues?.startTime || null,
  );
  const [end, setEnd] = useState<Moment | null>(initialValues?.endTime || null);
  const { form } = formObj;
  const { setFieldsValue, validateFields } = form;
  const currentAssignment = useAppSelector(
    state => state.appSettings.currentAssignment,
  );
  const [assignmentType, setAssignmentType] = useState<
    AssignmentEnumUi | undefined
  >(initialValues?.assignmentType);
  const [work, setWork] = useState<string | undefined>(initialValues?.workType);
  const [companyId, setCompanyId] = useState<string | undefined>(
    initialValues?.companyId || '',
  );
  const [searchAssignment, setSearchAssignment] = useState<string>('');
  const [searchCompany, setSearchCompany] = useState<string>('');

  const { data: assignments } = useGetTimeEntryAssignmentsQuery(
    {
      assignmentType,
      companyIds: [companyId || ''],
      search: searchAssignment,
    },
    {
      skip: !assignmentType,
    },
  );
  const { data: companyList, isLoading: companyLoading } =
    useSearchCompaniesInFilterQuery({
      sort: 'name,ASC',
      page: 0,
      pageSize: 2000,
      search: searchCompany || ' ',
    });
  const { data: workTypes, isLoading: workTypesLoading } =
    useGetWorkTypesQuery();
  // reads the full catalog, so a retired work type on an existing entry still resolves
  const { nameOf } = useWorkTypeNames();
  const companyOptions = useMemo(() => {
    if (companyList?.content.length) {
      return companyList?.content?.map(
        ({ id, nameWithAbbreviation, relationshipStatus }) => ({
          value: id,
          label: (
            <LabelWithStatus
              title={nameWithAbbreviation}
              status={relationshipStatus}
              statusColor={CompanyStatusMapperColor[relationshipStatus]}
            />
          ),
        }),
      );
    }
    return [];
  }, [companyList]);

  const currentAssignmentOptions = useMemo(() => {
    if (
      currentAssignment &&
      !assignments?.find(
        el => el.assignmentId === currentAssignment?.assignmentId,
      )
    ) {
      return [
        {
          value: currentAssignment.assignmentId,
          label: (
            <LabelWithStatus
              statusColor={getStatusColor(
                currentAssignment.assignmentType,
                currentAssignment.status,
              )}
              title={currentAssignment.name}
              status={currentAssignment.status}
            />
          ),
        },
      ];
    }
    return [];
  }, [currentAssignment]);

  const assignmentOptions = useMemo(() => {
    if (assignments?.length) {
      return assignments?.map(
        ({ assignmentId, name, status, assignmentType: type }) => ({
          value: assignmentId,
          label: (
            <LabelWithStatus
              statusColor={getStatusColor(type, status)}
              title={name}
              status={status}
            />
          ),
        }),
      );
    }
    return [];
  }, [assignments]);

  /**
   * The active catalog, plus the work type this entry already carries.
   *
   * Only active work types are offered, because the backend refuses a retired one on a new
   * entry. But an entry logged before a work type was retired still has to show its name when
   * reopened, and a value with no matching option renders as the raw key — a UUID, for a work
   * type the MSP created itself. So its own work type is added back, resolved from the full
   * catalog rather than the active list.
   */
  const workTypeOptions = useMemo(() => {
    const activeOptions = (workTypes || []).map(workType => ({
      value: workType.key,
      label: workType.name,
    }));
    const ownWorkType = initialValues?.workType;
    if (
      ownWorkType &&
      !activeOptions.some(option => option.value === ownWorkType)
    ) {
      return [
        { value: ownWorkType, label: nameOf(ownWorkType) },
        ...activeOptions,
      ];
    }
    return activeOptions;
  }, [workTypes, initialValues?.workType, nameOf]);

  useEffect(() => {
    if (
      work === NON_ASSIGNABLE_WORK_TYPE_KEYS.breakLunch ||
      work === NON_ASSIGNABLE_WORK_TYPE_KEYS.paidTimeOff
    ) {
      setFieldsValue({ assignmentType: undefined, assignmentId: undefined });
      setAssignmentType(undefined);
    }
  }, [work]);

  const onAssignmentTypeChange = async (value: AssignmentEnumUi) => {
    setAssignmentType(value);
    setFieldsValue({ assignmentId: undefined });
  };

  useEffect(() => {
    setAssignmentType(initialValues?.assignmentType);
  }, [initialValues?.assignmentType, initialValues?.assignmentId]);

  // Keep the local start/end (which drive the time bar) in sync with the
  // values the parent modal seeds into the form on open / entry change.
  // Depend on the primitive timestamps: EditTimeEntry rebuilds fresh Moment
  // refs every render, so keying on identity would clobber in-progress edits
  // on any incidental parent re-render.
  useEffect(() => {
    setStart(initialValues?.startTime || null);
    setEnd(initialValues?.endTime || null);
  }, [initialValues?.startTime?.valueOf(), initialValues?.endTime?.valueOf()]);

  // Applied when the draggable bar / quick-fill chips change the range:
  // update both the form store (for validation + submit) and local state.
  const handleRangeChange = (nextStart: Moment, nextEnd: Moment) => {
    setStart(nextStart);
    setEnd(nextEnd);
    setFieldsValue({ startTime: nextStart, endTime: nextEnd });
    validateFields(['startTime', 'endTime']).catch(() => undefined);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== 'Enter') return;
    const el = e.target as HTMLElement;
    // Let textareas keep newline behaviour and let selects use Enter to pick.
    if (el.tagName === 'TEXTAREA' || el.closest('.ant-select')) return;
    e.preventDefault();
    if (onSubmit) onSubmit();
  };

  return (
    <SCompactForm>
      <Form
        form={form}
        name="basic"
        initialValues={initialValues}
        autoComplete="off"
        onKeyDown={handleKeyDown}
      >
        <Row gutter={[10, 0]}>
          <Col xs={24} sm={24} md={24} lg={12}>
            <FormItem name="workType" label="Work type" rules={rules.workType}>
              <Select
                showSearch
                defaultValue="Select work type"
                placeholder="Select"
                options={workTypeOptions}
                onChange={value => setWork(value)}
                loading={workTypesLoading}
              />
            </FormItem>
          </Col>
          <Col xs={24} sm={24} md={24} lg={12}>
            <FormItem name="companyId" label="Company" rules={rules.companyId}>
              <Select
                showSearch
                defaultValue="Select company"
                options={companyOptions}
                loading={companyLoading}
                onChange={value => {
                  setCompanyId(value);
                  setFieldsValue({ assignmentId: undefined });
                  setFieldsValue({ assignmentType: undefined });
                  setAssignmentType(undefined);
                }}
                onSearch={debounce(input => {
                  setSearchCompany(input);
                }, 400)}
                filterOption={() => true}
              />
            </FormItem>
          </Col>
          <Col xs={24} sm={24} md={24} lg={12}>
            <FormItem name="startTime" label="Start" rules={rules.startTime}>
              <DateTimeInput
                viewMode={viewMode}
                onChange={value => setStart(value || null)}
              />
            </FormItem>
          </Col>
          <Col xs={24} sm={24} md={24} lg={12}>
            <FormItem name="endTime" label="End" rules={rules.endTime}>
              <DateTimeInput
                viewMode={viewMode}
                onChange={value => setEnd(value || null)}
              />
            </FormItem>
          </Col>
          {start && end && (
            <Col span={24}>
              <TimeRangeBar
                startTime={start}
                endTime={end}
                viewMode={viewMode}
                onViewModeChange={setViewMode}
                onChange={handleRangeChange}
              />
            </Col>
          )}
          <Col span={24}>
            <FormItem label="Ticket/Task/Project Task">
              <Group compact>
                <FormItem name="assignmentType" noStyle>
                  <Select
                    style={{ width: '30%' }}
                    disabled={
                      work === NON_ASSIGNABLE_WORK_TYPE_KEYS.breakLunch ||
                      work === NON_ASSIGNABLE_WORK_TYPE_KEYS.paidTimeOff ||
                      !companyId ||
                      !work
                    }
                    onChange={onAssignmentTypeChange}
                    defaultValue="Select type"
                    placeholder="Select type"
                    options={[
                      { label: 'Ticket', value: AssignmentEnumUi.Ticket },
                      { label: 'Task', value: AssignmentEnumUi.Task },
                      {
                        label: 'Project Task',
                        value: AssignmentEnumUi['Project Task'],
                      },
                    ]}
                  />
                </FormItem>
                <FormItem noStyle name="assignmentId">
                  <Select
                    style={{ width: '70%' }}
                    showSearch
                    disabled={!assignmentType}
                    options={[
                      ...currentAssignmentOptions,
                      ...assignmentOptions,
                    ]}
                    placeholder="Select ticket/task/project task"
                    onSearch={debounce(input => {
                      setSearchAssignment(input);
                    }, 400)}
                    filterOption={() => true}
                  />
                </FormItem>
              </Group>
            </FormItem>
          </Col>
          <Col span={24}>
            <FormItem name="note" label="Description" rules={rules.note}>
              <TextArea
                rows={6}
                placeholder="Describe the work done…"
                style={{ resize: 'vertical', minHeight: 120 }}
              />
            </FormItem>
          </Col>
        </Row>
      </Form>
    </SCompactForm>
  );
};

export default CreateTimeEntryForm;
